import { useState, useEffect, useRef } from 'react';
import { Check } from 'lucide-react';

interface DropdownPickerItem {
  id: string;
  label: string;
  color?: string;
}

interface DropdownPickerProps {
  items: DropdownPickerItem[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  onClose: () => void;
  placeholder?: string;
}

export function DropdownPicker({
  items,
  selectedIds,
  onToggle,
  onClose,
  placeholder = 'Filter...',
}: DropdownPickerProps) {
  const [filter, setFilter] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = filter.trim()
    ? items.filter(item => item.label.toLowerCase().includes(filter.toLowerCase()))
    : items;

  return (
    <div
      ref={ref}
      className="absolute z-30 top-full left-0 mt-1 w-full bg-bg-surface border border-border rounded-lg shadow-lg"
    >
      <div className="px-2 pt-2 pb-1">
        <input
          ref={inputRef}
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') onClose();
          }}
          placeholder={placeholder}
          className="w-full px-2 py-1.5 bg-bg-input border border-border rounded text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
        />
      </div>
      <div className="max-h-48 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="px-3 py-2 text-sm text-text-muted">
            {items.length === 0 ? 'None available' : 'No matches'}
          </div>
        ) : (
          filtered.map(item => {
            const selected = selectedIds.includes(item.id);
            return (
              <button
                key={item.id}
                onClick={() => onToggle(item.id)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-bg-hover transition-colors"
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${selected ? 'bg-accent border-accent' : 'border-border'}`}>
                  {selected && <Check size={10} className="text-white" />}
                </div>
                {item.color && <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />}
                <span className="text-text-primary">{item.label}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
